import React, { PureComponent } from 'react';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import queryString from 'qs';
import { actions as accountActions } from 'reducers/Account';
import { BubbleIcon } from 'Common/components/Molecules/BubbleIcon';
import { Card } from 'Common/components/Atoms/Card';
import { Flex } from 'Common/components/Atoms/Flex';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { Link } from 'Common/components/Atoms/Link';
import { Toast } from 'Common/components/Molecules/Toast';
import { Text } from 'Common/components/Atoms/Text';
import { Footer } from '../Shared/Footer';
import { FooterBullet } from '../Shared/FooterBullet';
import { Subtitle } from '../Shared/Subtitle';
import { Title } from '../Shared/Title';
import { Email } from 'constants/email';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelopeOpenText } from '@fortawesome/pro-solid-svg-icons';
import { mail } from 'fluent-mailto';
import Bowser from 'bowser';
import dayjs from 'dayjs';
import { EMAIL_VERIFICATION } from 'DisplayText/ReviewerOnboarding';
import {
  accountErrorSelector,
  accountSelector,
  isUpdatingAccountSelector,
} from '../../../selectors/Account';
import { HttpError } from 'sagas/Clients/HttpError';

interface Props {
  userName?: string;
  email?: string;
  error?: string | HttpError;
  isUpdating: boolean;
  isLoggedIn: boolean;
  isVerified: boolean;
  isVerificationFailed: boolean;
}

interface OwnProps {
  location: any;
}

interface DispatchProps {
  fetchAccount: () => void;
  resendEmail: () => void;
}

type AllProps = Props & OwnProps & DispatchProps;

interface State {
  showToast: boolean;
}

export class EmailVerificationStep extends PureComponent<AllProps, State> {
  state = { showToast: false };
  private readonly baseMailto = mail
    .to(Email.SUPPORT)
    .subject(EMAIL_VERIFICATION.SUBJECT);

  componentDidMount() {
    const { fetchAccount } = this.props;
    /**
     * Call fetchAccount in case someone is reloading this email verify page. If someone is now logged in
     * and verified, then this will automatically redirect to the dashboard.
     */
    fetchAccount();
  }

  componentDidUpdate(prevProps: AllProps) {
    if (!this.props.isUpdating && prevProps.isUpdating && !this.props.error) {
      this.setState({ showToast: true });
    }
  }

  private submit = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    const { isUpdating } = this.props;
    if (isUpdating) {
      return;
    }

    this.props.resendEmail();
  };

  private getMailto(): string {
    const { email, userName } = this.props;
    const { browser, os, platform } = Bowser.parse(window.navigator.userAgent);
    return this.baseMailto
      .body(
        `Hi PullRequest Team,
          
My name is ${userName} and I'm unable to verify my email. 

Debugging details:

- Date and Time: ${dayjs()}
- Email address: ${email}
- Browser: ${browser.name} ${browser.version}
- OS: ${os.name} ${os.version} ${os.versionName}
- Platform: ${platform.type}

Sincerely, ${userName}`
      )
      .build();
  }

  render = () => {
    const { isLoggedIn, isVerified, isUpdating, isVerificationFailed, email } =
      this.props;

    if (isVerified) {
      return <Redirect to="/dash" />;
    }

    if (!isLoggedIn) {
      return <Redirect to="/login" />;
    }

    return (
      <div>
        <Title>{EMAIL_VERIFICATION.TITLE}</Title>
        <Subtitle>{EMAIL_VERIFICATION.SUBTITLE}</Subtitle>
        <Card display="block" width={480} m="auto" px={6} py={4}>
          <BubbleIcon
            icon={faEnvelopeOpenText}
            size="5x"
            p={4}
            mx="auto"
            mb={3}
          />
          <Text variant="body1" mb={2}>
            {EMAIL_VERIFICATION.EXPLANATORY_TEXT}
          </Text>
          <Flex my={3} justifyContent="center" alignItems="center">
            <FontIcon icon={faEnvelope} size="1x" mr={1} />
            <Text variant="body1">
              <b>{email}</b>
            </Text>
          </Flex>
          <Footer>
            <FooterBullet>
              Didn't receive an email? <Link onClick={this.submit}>Resend</Link>{' '}
            </FooterBullet>
            <FooterBullet>
              Can't verify your email? Email{' '}
              <Link href={this.getMailto()}>{Email.SUPPORT}</Link>
            </FooterBullet>
          </Footer>
        </Card>
        {isVerificationFailed && (
          <Text variant="body1" color="error" my={3} textAlign="center">
            {EMAIL_VERIFICATION.VERIFICATION_FAILED}
          </Text>
        )}
        <Toast
          severity="success"
          showToast={this.state.showToast}
          handleClose={() => this.setState({ showToast: false })}
          message={EMAIL_VERIFICATION.RESENT.replace('{EMAIL}', email || '')}
          horizontal="center"
          vertical="bottom"
          icon={faEnvelope}
        />
        <Toast
          severity="info"
          showToast={isUpdating}
          handleClose={() => {
            /** do nothing */
          }}
          message={EMAIL_VERIFICATION.RESENDING}
          horizontal="center"
          vertical="bottom"
          icon={faEnvelope}
        />
      </div>
    );
  };
}

function mapStateToProps(state: any, ownProps: OwnProps) {
  const account = accountSelector(state);
  const { user } = account;
  const isUpdating = isUpdatingAccountSelector(state);
  const error = accountErrorSelector(state);
  return {
    isUpdating,
    userName: user?.name,
    isVerified: user?.email_verified ?? false,
    isLoggedIn: !!user,
    email: user?.email,
    error,
    isVerificationFailed: Boolean(
      queryString.parse(ownProps.location.search).failed
    ),
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return bindActionCreators({ ...accountActions }, dispatch);
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmailVerificationStep);
